import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VisitsReportFiltersScreen.module.scss';
import Button from '../../../common/components/button/Button';
import FormItem from '../../../common/components/formItem/FormItem';
import Label from '../../../common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../common/types/SelectValueLabel';
import { Controller, useForm } from 'react-hook-form';
import Logger from 'common/services/Logger';
import { DATEPICKER_FORMAT_DEFAULT, LOGGER_LOG_TYPE, removeAccents } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import GeofencesService from 'api/geofences/GeofencesService';
import Select from 'common/components/select/Select';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import moment from 'moment';
import DriversService from 'api/drivers/DriversService';
import SelectController from 'common/components/select/SelectController';
import VehiclesService from 'api/vehicles/VehiclesService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import LocalsService from 'api/locals/LocalsService';
import { FaInfoCircle } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import { useSelector } from 'react-redux';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import UsersService from 'api/users/UsersService';
import Utils from 'common/services/Utils';
export interface Filters {
    vehicleId?: string;
    vehicleName?: string;
    geofenceId?: string;
    geofenceName?: string;
    entryDate?: Date;
    exitDate?: Date;
    page?: number;
    scrollToItemId?: string;

    driverId?: string;
    driverName?: string;
    durationStart?: number;
    durationStartLabel?: string;
    durationEnd?: number;
    durationEndLabel?: string;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    segmentId?: string;
    segmentName?: string;
    orderBy?: string;
    orderColumn?: string;
}

type Props = {
    filters: Filters;
    hasCountersPolicy?: boolean;
    onChange: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
    hideGeofenceSelector: boolean;
}

const VisitsReportFiltersScreen: React.FC<Props> = ({ filters, onChange, onFilter, hideGeofenceSelector }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { getValues, setValue, reset, watch, control } = form;
    const [geofences, setGeofences] = useState<SelectValueLabel[]>([]);
    const [drivers, setDrivers] = useState<SelectValueLabel[]>([]);
    const [vehiclesOptions, setVehicles] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegments] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocals] = useState<SelectValueLabel[]>([]);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasReadCostCenterPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_COST_CENTER_READ']);

    const getData = async () => {
        try {
            Loading.show();
            const [geofencesDB, driversList, vehiclesOptions, segmentsOptionsDB, localsOptionsDB] = await Promise.all([
                hasReadCostCenterPolicy ? GeofencesService.catalog() : [],
                DriversService.catalog(),
                VehiclesService.catalogByVisitsReports({}),
                VehicleSegmentsService.catalog(),
                LocalsService.catalog(),
            ]);
            setGeofences(geofencesDB);
            setDrivers(driversList);
            setVehicles(vehiclesOptions);
            setSegments(Utils.sortAlphabetically(segmentsOptionsDB));
            setLocals([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(localsOptionsDB)]);

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VEHICLES, `Couldn't get geofences`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
        finally {
            Loading.hide();
        }
    }

    useEffect(() => {
        getData();
    }, []);

    const clearFilters = () => {
        reset({
            vehicleName: undefined,
            geofenceId: undefined,
            geofenceName: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            segmentId: undefined,
            segmentName: undefined
        });
        onInputChange();
        onSubmit();
    }

    const onSubmit = () => {
        onFilter(getValues());
    }

    const onInputChange = () => {
        onChange(getValues());
    }

    const getVal = (arr: any[] | undefined, index: number): any | undefined => {
        return arr && arr.length > index ? arr[index] : undefined;
    }


    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row>
                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.vehicle')}
                                        <FaInfoCircle data-tip={t('visits_report.list.only_vehicles_with_visits_reports')} data-for={'activesTooltip'} style={{ marginLeft: '0.5rem' }} />
                                    </Label>
                                    <ReactTooltip id={'activesTooltip'} place="right" type="dark" effect="solid" />
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={vehiclesOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.vehicle')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('vehicleName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={vehiclesOptions.find(x => x.value === value) ? { label: vehiclesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="vehicleId"
                                        defaultValue={getValues('vehicleId')} />
                                </FormItem>
                            </Col>
                            {hasReadCostCenterPolicy && !hideGeofenceSelector && <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('visits_report.list.stopping_area')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={geofences}
                                                    isClearable
                                                    placeholder={t('visits_report.list.stopping_area')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('geofenceName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={geofences.find(x => x.value === value) ? { label: geofences.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="geofenceId"
                                        defaultValue={getValues('geofenceId')} />
                                </FormItem>
                            </Col>}
                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('visits_report.list.visit_date')}
                                        onChange={(startValue: any, endValue: any) => {
                                            setValue('entryDate', startValue ? moment(startValue).add(1, 'hour').toDate() : undefined);
                                            setValue('exitDate', endValue ? moment(endValue).add(1, 'hour').toDate() : undefined);
                                            onInputChange();
                                        }}
                                        defaultStartValue={watch('entryDate')}
                                        defaultEndValue={watch('exitDate')}
                                        dateFormat={DATEPICKER_FORMAT_DEFAULT}
                                        isDatePicker
                                    />
                                </FormItem>
                            </Col>

                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('car_verification.verification.driver')}</Label>
                                    <SelectController
                                        form={form}
                                        isClearable={true}
                                        name='driverId'
                                        options={drivers}
                                        placeholder={t('car_verification.verification.driver')}
                                        isDisabled={false}
                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                        onChange={data => {
                                            form.setValue('driverName', data ? data.label : null);
                                            onInputChange();
                                        }}
                                    />
                                </FormItem>
                            </Col>

                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={segmentsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('segmentName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={segmentsOptions.find(x => x.value === value) ? { label: segmentsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="segmentId"
                                        defaultValue={getValues('segmentId')} />
                                </FormItem>
                            </Col>
                            <Col xs={6}>
                                <FormItem>
                                    <div className={styles.labelWithOperator}>
                                        <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                        <div className={styles.operatorToggle}>
                                            <span title={t('common.filter_all_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_all' as any) as string}
                                                    className={styles.firstChild}
                                                    preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                            <span title={t('common.filter_any_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_any' as any) as string}
                                                    className={styles.lastChild}
                                                    preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={localsOptions}
                                                    isMulti
                                                    isClearable
                                                    placeholder={t('vehicle.local')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        onSelectChange(data?.map(d => d.value) ?? []);
                                                        setValue('localNames', data?.map(d => d.label) ?? []);
                                                        onInputChange();
                                                    }}
                                                    value={localsOptions.filter(x => (value || []).includes(x.value))}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="localIds"
                                        defaultValue={getValues('localIds') || []} />
                                </FormItem>
                            </Col>
                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('common.duration') + (' (h)')}
                                        onChange={(startValue: any, endValue: any) => {
                                            setValue('durationStartLabel', startValue);
                                            setValue('durationEndLabel', endValue);
                                            onInputChange();
                                        }}
                                        defaultStartValue={watch('durationStartLabel')}
                                        defaultEndValue={watch('durationEndLabel')}
                                        preset="duration"
                                        isTimePicker={true}
                                    />
                                </FormItem>
                            </Col>
                        </Row>
                    </FormItem>
                </div>

                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form >
    );
}

export default memo(VisitsReportFiltersScreen);
